import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Tooltip,
  Chip,
  IconButton,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  WarningAmber as WarningAmberIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import GroupedSelect from "./GroupedSelect";
import { TextFieldForViewOnly } from "./TextField/TextField";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { ScheduleTaggingContext } from "../Common/TaxAdjustment/ScheduleTaggingProvider";
import { isLinkedRowDirty } from "../Common/TaxAdjustment/services/scheduleTaggingService";
import {
  taxAdjustmentOptions_tagging,
  investmentTaxAdjustmentOptions_tagging,
} from "../../utils/static/detailsPL";

// Enhanced GroupedSelect wrapper with built-in validation
const AdjustmentTaggingSelect = ({
  value,
  onChange,
  company,
  validationRules = [],
  validationContext = {},
  showValidationIcon = true,
  showValidationChip = false,
  validationIconSlotWidth = 28,
  selectTooltipTitle,
  size = "small",
  placeholder = "Please select...",
  sx = {},
  disabled = false,
  fullWidth = false,
  canEdit = true,
  onOpenTaggingDialog,
  linkedRowSource,
  onSyncLinkedRow,
  ...otherProps
}) => {
  const theme = useTheme();
  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const tagging = useContext(ScheduleTaggingContext);
  const canUpdateLinked = tagging?.canUpdateLinkedRow(linkedRowSource);
  const needsSync = Boolean(canUpdateLinked && isLinkedRowDirty(linkedRowSource?.row));
  const isUpdatingLinked = Boolean(tagging?.isLinkedRowUpdating(linkedRowSource));
  const showSyncAction = Boolean(canUpdateLinked && needsSync);
  const options = company?.companyType === "investment"
    ? investmentTaxAdjustmentOptions_tagging
    : taxAdjustmentOptions_tagging;
  const tooltipTitle = selectTooltipTitle ?? (
    value ? options.find((option) => option.value === value)?.label || value : placeholder
  );
  const actionSlotWidth = Math.max(28, validationIconSlotWidth);

  // Validation logic
  const validation = useMemo(() => {
    if (!validationRules.length) return { isValid: true, level: "none" };

    for (const rule of validationRules) {
      const result = rule.validator(value, validationContext);
      if (!result.isValid) {
        return {
          isValid: false,
          level: rule.level || "warning",
          message: result.message || rule.message,
          action: rule.action,
          icon: rule.icon,
          severity: rule.level || "warning",
        };
      }
    }

    return { isValid: true, level: "success" };
  }, [value, validationContext, validationRules]);

  // Get validation styling
  const getValidationStyles = () => {
    if (validation.isValid) return {};

    const colors = {
      error: {
        bg: alpha(theme.palette.error.light, 0.06),
        bgHover: alpha(theme.palette.error.light, 0.1),
        bgFocus: alpha(theme.palette.error.light, 0.12),
        border: alpha(theme.palette.error.main, 0.4),
        borderHover: alpha(theme.palette.error.main, 0.6),
        borderFocus: theme.palette.error.main,
        text: theme.palette.error.main,
        icon: theme.palette.error.main,
        shadow: alpha(theme.palette.error.main, 0.15),
        accent: theme.palette.error.main,
      },
      warning: {
        bg: alpha(theme.palette.warning.light, 0.06),
        bgHover: alpha(theme.palette.warning.light, 0.1),
        bgFocus: alpha(theme.palette.warning.light, 0.12),
        border: alpha(theme.palette.warning.main, 0.4),
        borderHover: alpha(theme.palette.warning.main, 0.6),
        borderFocus: theme.palette.warning.main,
        text: theme.palette.warning.dark,
        icon: theme.palette.warning.main,
        shadow: alpha(theme.palette.warning.main, 0.15),
        accent: theme.palette.warning.main,
      },
      info: {
        bg: alpha(theme.palette.info.light, 0.06),
        bgHover: alpha(theme.palette.info.light, 0.1),
        bgFocus: alpha(theme.palette.info.light, 0.12),
        border: alpha(theme.palette.info.main, 0.4),
        borderHover: alpha(theme.palette.info.main, 0.6),
        borderFocus: theme.palette.info.main,
        text: theme.palette.info.main,
        icon: theme.palette.info.main,
        shadow: alpha(theme.palette.info.main, 0.15),
        accent: theme.palette.info.main,
      },
    };

    const selectedColor = colors[validation.severity] || colors.error;

    return {
      // Base select styling
      backgroundColor: selectedColor.bg,
      transition: "all 0.2s ease-in-out",
      position: "relative",

      // Animated pulse effect for errors
      ...(validation.severity === "error" && {
        animation: "validationPulse 2s ease-in-out infinite",
        "@keyframes validationPulse": {
          "0%, 100%": {
            boxShadow: `0 0 0 0 ${alpha(selectedColor.accent, 0.4)}`,
          },
          "50%": {
            boxShadow: `0 0 0 4px ${alpha(selectedColor.accent, 0.1)}`,
          },
        },
      }),

      "&:hover": {
        backgroundColor: selectedColor.bgHover,
        boxShadow: `0 0 0 1px ${selectedColor.borderHover}`,
      },

      "&.Mui-focused": {
        backgroundColor: selectedColor.bgFocus,
        boxShadow: `0 0 0 2px ${selectedColor.shadow}`,
      },

      "& .MuiSelect-select": {
        color: selectedColor.text,
        fontWeight: 500,
        "&::placeholder": {
          color: alpha(selectedColor.text, 0.6),
        },
      },

      "& .MuiSelect-icon": {
        color: selectedColor.icon,
        opacity: 0.8,
      },

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: selectedColor.border,
        borderWidth: "1.5px",
        transition: "all 0.2s ease-in-out",
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: selectedColor.borderHover,
        borderWidth: "1.5px",
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: selectedColor.borderFocus,
        borderWidth: "2px",
      },

      // Validation indicator accent
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        backgroundColor: selectedColor.accent,
        borderRadius: "6px 6px 0 0",
        zIndex: 1,
        opacity: 0.8,
      },

      // Enhanced MenuProps for validation states
      "& .MuiMenu-paper": {
        border: `1px solid ${selectedColor.border}`,
        boxShadow: `0 8px 24px ${alpha(selectedColor.accent, 0.12)}`,

        "& .MuiListSubheader-root": {
          backgroundColor: alpha(selectedColor.bg, 0.95),
          borderBottom: `1px solid ${alpha(selectedColor.border, 0.3)}`,
          color: selectedColor.text,
        },

        "& .MuiMenuItem-root": {
          "&.Mui-selected": {
            backgroundColor: alpha(selectedColor.accent, 0.12),
            color: selectedColor.text,

            "&::before": {
              backgroundColor: selectedColor.accent,
            },

            "&:hover": {
              backgroundColor: alpha(selectedColor.accent, 0.18),
            },
          },

          "&:hover": {
            backgroundColor: alpha(selectedColor.accent, 0.08),
          },
        },
      },
    };
  };

  // Get validation icon
  const getValidationIcon = () => {
    if (validation.isValid)
      return <CheckCircleIcon sx={{ color: "success.main" }} />;

    if (validation.icon) return validation.icon;

    const iconProps = { sx: { color: `${validation.level}.main` } };

    switch (validation.level) {
      case "error":
        return <ErrorIcon {...iconProps} />;
      case "info":
        return <InfoIcon {...iconProps} />;
      default:
        return <WarningAmberIcon {...iconProps} />;
    }
  };

  // Enhanced select styles
  const enhancedSx = {
    ...getValidationStyles(),
    ...sx,
  };

  const ValidationIcon = () => (
    <Tooltip
      title={validation.message || "Validation status"}
      arrow
      placement='top'
    >
      <IconButton
        size='small'
        sx={{
          p: 0.25,
          flexShrink: 0,
          height: validationIconSlotWidth,
          width: validationIconSlotWidth,
          "&:hover": { backgroundColor: "transparent" },
          cursor: "pointer",
        }}
        onClick={onOpenTaggingDialog}
        disabled={disabled || isUpdatingLinked}
      >
        {getValidationIcon()}
      </IconButton>
    </Tooltip>
  );

  const ValidationChip = () =>
    validation.level !== "success" && (
      <Chip
        size='small'
        variant='outlined'
        color={validation.level}
        label={validation.message}
        icon={getValidationIcon()}
        sx={{
          height: 24,
          fontSize: "0.75rem",
          "& .MuiChip-icon": { fontSize: 16 },
        }}
      />
    );

  if (!canEdit) {
    return <TextFieldForViewOnly value={value || "N/A"} />;
  }

  const selectField = (
    <Box sx={{ flex: fullWidth ? 1 : "none", minWidth: 0 }}>
      <GroupedSelect
        value={value}
        onChange={onChange}
        options={options}
        size={size}
        placeholder={placeholder}
        sx={enhancedSx}
        disabled={disabled || isUpdatingLinked}
        fullWidth={fullWidth}
        onDropdownOpen={() => setSelectMenuOpen(true)}
        onDropdownClose={() => setSelectMenuOpen(false)}
        {...otherProps}
      />

      {showValidationChip && (
        <Box sx={{ mt: 0.5 }}>
          <ValidationChip />
        </Box>
      )}
    </Box>
  );

  return (
    <Stack
      direction='row'
      spacing={1}
      alignItems='center'
      sx={(theme) => ({
        width: fullWidth ? "100%" : "auto",
        "& [data-linked-row-update-action]": {
          display: "inline-flex",
          flexShrink: 0,
          transition: theme.transitions.create("opacity", { duration: 150 }),
          // Keep the slot and keyboard access while reducing noise in tables.
          "@media (hover: hover) and (pointer: fine)": {
            opacity: isUpdatingLinked || needsSync ? 1 : 0,
            pointerEvents: isUpdatingLinked || needsSync ? "auto" : "none",
          },
          "@media (any-pointer: coarse)": {
            opacity: 1,
            pointerEvents: "auto",
          },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        },
        // Exclude enclosing layout rows when a table contains nested tables.
        "&:hover [data-linked-row-update-action], &:focus-within [data-linked-row-update-action], tr:hover:not(:has(tr:hover)) & [data-linked-row-update-action], tr:focus-within:not(:has(tr:focus-within)) & [data-linked-row-update-action]": {
          opacity: 1,
          pointerEvents: "auto",
        },
      })}
    >
      {tooltipTitle ? (
        <Tooltip
          title={selectMenuOpen ? "" : tooltipTitle}
          placement='top'
          arrow
          disableInteractive
          disableFocusListener={selectMenuOpen}
          disableHoverListener={selectMenuOpen}
        >
          {selectField}
        </Tooltip>
      ) : (
        selectField
      )}

      {/* Always reserve the same space, including rows without an action. */}
      <Stack alignItems='center' justifyContent='center' sx={{ width: actionSlotWidth, flexShrink: 0 }}>
        {showValidationIcon && !validation.isValid && <ValidationIcon />}

        {showSyncAction && (
          <Tooltip
            title={
              isUpdatingLinked
                ? "Updating linked schedule…"
                : "Name or amount changed. Sync to update the linked schedule."
            }
            arrow
          >
            <Box component='span' data-linked-row-update-action>
              <IconButton
                size='small'
                aria-label='Update linked schedule'
                disabled={disabled || isUpdatingLinked}
                onClick={() => {
                  const run = onSyncLinkedRow
                    ? onSyncLinkedRow(linkedRowSource)
                    : tagging.updateLinkedRow(linkedRowSource);
                  void Promise.resolve(run).catch(() => {});
                }}
                sx={{
                  width: 28,
                  height: 28,
                  color: isUpdatingLinked ? "warning.main" : "warning.main",
                  "&:hover, &.Mui-focusVisible": { color: "warning.dark" },
                }}
              >
                {isUpdatingLinked
                  ? <CircularProgress size={16} color='inherit' />
                  : <SyncRoundedIcon sx={{ fontSize: 19 }} />}
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export const createValidationRules = {
  // Tax adjustment validation
  requireScheduleForCustomAdjustment: (
    allowedValues = ["", "Added Back", "Deduct"]
  ) => ({
    level: "warning",
    message: "This row needs to be tagged to a schedule",
    validator: (value, context) => ({
      isValid: allowedValues.includes(value) || Boolean(context.schedule),
    }),
  }),

  // Required field validation
  required: (message = "This field is required") => ({
    level: "error",
    message,
    validator: (value) => ({
      isValid: Boolean(value && value.toString().trim()),
    }),
  }),

  // Custom validation
  custom: (validatorFn, message, level = "warning") => ({
    level,
    message,
    validator: validatorFn,
  }),

  // Value in list validation
  mustBeOneOf: (allowedValues, message) => ({
    level: "error",
    message: message || `Must be one of: ${allowedValues.join(", ")}`,
    validator: (value) => ({
      isValid: allowedValues.includes(value),
    }),
  }),

  // Conditional requirement
  requiredWhen: (condition, message = "Required") => ({
    level: "warning",
    message,
    validator: (value, context) => ({
      isValid: !condition(context) || Boolean(value && value.toString().trim()),
    }),
  }),
};

export default AdjustmentTaggingSelect;
